import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import AuthContext from "./authProvider";
import { deleteTablePreference, fetchTablePreferences, saveTablePreference } from "../components/table/preferencesApi";

/**
 * Layouts de colunas do usuário logado: cache em localStorage (abre certo na hora)
 * + servidor (vale em qualquer máquina). Falhas de rede não travam a tela.
 */
export const TablePreferencesContext = createContext(null);

const cacheKeyFor = (email) => (email ? `mgs.tablePreferences.${email}` : null);

const readCache = (key) => {
    if (!key) return {};
    try {
        return JSON.parse(window.localStorage.getItem(key)) ?? {};
    } catch {
        return {};
    }
};

const writeCache = (key, value) => {
    if (!key) return;
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // navegador sem storage: segue só com o servidor
    }
};

export const TablePreferencesProvider = ({ children }) => {
    const { user } = useContext(AuthContext);
    const cacheKey = cacheKeyFor(user?.email);
    const tokenRef = useRef(user?.token);
    tokenRef.current = user?.token;
    const [layouts, setLayouts] = useState(() => readCache(cacheKey));
    const layoutsRef = useRef(layouts);
    layoutsRef.current = layouts;

    const commit = useCallback((next) => {
        layoutsRef.current = next;
        setLayouts(next);
        writeCache(cacheKey, next);
    }, [cacheKey]);

    useEffect(() => {
        const cached = readCache(cacheKey);
        layoutsRef.current = cached;
        setLayouts(cached);
        if (!cacheKey || !tokenRef.current) return undefined;
        let cancelled = false;
        fetchTablePreferences(tokenRef.current)
            .then((data) => {
                if (!cancelled) commit(data);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [cacheKey, commit]);

    const saveLayout = useCallback((tableKey, layout) => {
        commit({ ...layoutsRef.current, [tableKey]: layout });
        if (tokenRef.current) saveTablePreference(tokenRef.current, tableKey, layout).catch(() => {});
    }, [commit]);

    const resetLayout = useCallback((tableKey) => {
        const { [tableKey]: _removed, ...rest } = layoutsRef.current;
        commit(rest);
        if (tokenRef.current) deleteTablePreference(tokenRef.current, tableKey).catch(() => {});
    }, [commit]);

    const value = useMemo(() => ({ layouts, saveLayout, resetLayout }), [layouts, saveLayout, resetLayout]);
    return <TablePreferencesContext.Provider value={value}>{children}</TablePreferencesContext.Provider>;
};

export const useTablePreferences = () => useContext(TablePreferencesContext);
